import React, { useState } from "react";
import { useCart } from "../../context/CartContext";
import { FaTrashAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import ShippingStep from "./ShippingStep";

export type StripeTotals = {
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  stripe_tax_calculation_id?: string;
  stripe_payment_intent_id?: string;
};

const formatMoney = (cents: number) => (cents / 100).toFixed(2);

const Cart = () => {
  const { cart, removeFromCart, updateQuantity } = useCart();

  const [stripeTotals, setStripeTotals] = useState<StripeTotals | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const handleQuantityChange = (
    id: string,
    variantId: string | undefined,
    value: string
  ) => {
    const parsed = parseInt(value, 10);
    const newQty = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;

    updateQuantity(id, newQty, variantId);

    setStripeTotals(null);
    setClientSecret(null);
  };

  const localSubtotal = cart.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-[#5c2f2f] mb-6">Your Cart</h1>

      {cart.length === 0 ? (
        <p className="text-gray-500">Your cart is empty.</p>
      ) : (
        <div className="mb-8 space-y-4">
          {cart.map((item) => (
            <div
              key={`${item.id}-${item.variant_id || "no-variant"}`}
              className="flex items-center justify-between border border-[rgba(92,47,47,0.2)] p-4 rounded bg-white/90 shadow-[0_1px_6px_rgba(92,47,47,0.08)] hover:shadow-[0_3px_12px_rgba(92,47,47,0.15)] transition"
            >
              <div className="flex items-center gap-4">
                <Link to={`/product/${item.id}`}>
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-contain rounded"
                  />
                </Link>

                <div>
                  <p className="font-semibold text-[#3f1f1f]">{item.name}</p>

                  {item.size_label && (
                    <p className="text-sm text-gray-500 mt-1">
                      Size: {item.size_label}
                    </p>
                  )}

                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="number"
                      value={item.quantity}
                      min={1}
                      onChange={(e) =>
                        handleQuantityChange(
                          item.id,
                          item.variant_id,
                          e.target.value
                        )
                      }
                      className="w-16 border border-[rgba(92,47,47,0.3)] rounded p-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#a25d5d]"
                    />

                    <button
                      onClick={() => {
                        removeFromCart(item.id, item.variant_id);
                        setStripeTotals(null);
                        setClientSecret(null);
                      }}
                      className="text-[#a25d5d] hover:text-[#8f4d4d] transition"
                      title="Remove"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </div>

              <div className="font-semibold text-[#3f1f1f] text-right">
                ${(item.price * item.quantity).toFixed(2)}
              </div>
            </div>
          ))}

          <div className="mt-4 border-t pt-4 text-right text-[#3f1f1f]">
            {stripeTotals ? (
              <div className="space-y-1">
                <div className="text-base">
                  Subtotal: ${formatMoney(stripeTotals.subtotal_cents)}
                </div>
                <div className="text-base">
                  Shipping: ${formatMoney(stripeTotals.shipping_cents)}
                </div>
                <div className="text-base">
                  Tax: ${formatMoney(stripeTotals.tax_cents)}
                </div>
                <div className="text-xl font-bold">
                  Total: ${formatMoney(stripeTotals.total_cents)}
                </div>
              </div>
            ) : (
              <div className="text-xl font-bold">
                Subtotal: ${localSubtotal.toFixed(2)}
              </div>
            )}
          </div>
        </div>
      )}

      <ShippingStep
        onReadyForPayment={({ totals, clientSecret }) => {
          setStripeTotals(totals);
          setClientSecret(clientSecret);
        }}
        clientSecret={clientSecret}
      />
    </div>
  );
};

export default Cart;